import { notEmpty } from '@mahawi/eshop-common/src/not-empty';
import { type RootState } from '@reducers';
import { Col, Form, Row, Select, Space } from 'antd';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { savePropertyCodes } from '../../reducers/category/reducer';
import { SPACING } from '../../theme/tokens';
import FormSubmitFooter from '../form-submit-footer';
import Link from '../link';

interface IForm {
  propertyCodes: string[];
}

function PropertiesForm(): React.ReactElement {
  const dispatch = useAppDispatch();
  const propertyCodes = useSelector((s: RootState) => s.Properties.codes);
  const category = useSelector((s: RootState) => s.Category.category);
  const categoryInProcess = useSelector((s: RootState) => s.Category.inProcess);
  const categoryUpdatedAt = useSelector((s: RootState) => s.Category.updatedAt);

  const [form] = Form.useForm<IForm>();

  const onFinish = useCallback(
    ({ propertyCodes: codes }: IForm): void => {
      if (category?.uuid) {
        dispatch(savePropertyCodes(category.uuid, codes));
      }
    },
    [category?.uuid, dispatch],
  );

  const linkedCodes =
    category?.property.codes
      .map((code: string): React.ReactElement => (
        <div key={code}>
          <Link to={`/property/${code}`} label={code} openInNewTab />
        </div>
      ))
      .filter(notEmpty) ?? [];

  return (
    <Form
      form={form}
      initialValues={{ propertyCodes: category?.property.codes ?? [] }}
      onFinish={(values: IForm): void => onFinish(values)}
      layout="vertical"
    >
      <Row>
        <Col xs={24} md={8}>
          <Form.Item
            name="propertyCodes"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="Select properties"
              disabled={categoryInProcess}
              showSearch={{ autoClearSearchValue: false }}
              options={propertyCodes.map((code: string) => ({
                value: code,
                label: code,
              }))}
            />
          </Form.Item>
        </Col>

        <Col span={15} offset={1}>
          {linkedCodes.length > 0 && (
            <Space orientation="vertical" size={SPACING.xs}>
              {linkedCodes}
            </Space>
          )}
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <FormSubmitFooter
            inProcess={categoryInProcess}
            updatedAt={categoryUpdatedAt}
          />
        </Col>
      </Row>
    </Form>
  );
}

function PropertiesComponent(): React.ReactElement {
  return <PropertiesForm />;
}

export default PropertiesComponent;
